/*eslint max-lines-per-function: ["error", 180]*/
/*eslint complexity: ["error", 15]*/
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Container, Row, Col } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import * as Icon from 'react-bootstrap-icons';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { StampType } from '../../../common/spdCore/validation/schemas';
import {
  GetStampDto,
  UploadStampsDto,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { StampAlerts } from '../../components/StampManagement/StampAlerts';
import { StampDisplay } from '../../components/StampManagement/StampDisplay';
import { StampActions } from '../../components/StampManagement/StampActions';
import { Waiting } from '../../../common/components/Waiting/Waiting';

const MAX_KB_SIZE = 500;
const MAX_STAMP_SIZE_BYTES = MAX_KB_SIZE * 1024; // 500kb

export const StampManagementView: React.FC = () => {
  const spd = useSpdCore();
  const { userId } = useParams<{ userId?: string }>();

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [bigStamp, setBigStamp] = useState<string>('');
  const [smallStamp, setSmallStamp] = useState<string>('');
  const [doubleStamp, setDoubleStamp] = useState<string>('');
  const [signatureStamp, setSignatureStamp] = useState<string>('');
  const isEditingOtherUser = !!userId;

  const [titleText, setTitleText] = useState<string>(
    isEditingOtherUser
      ? 'Zarządzanie pieczątkami użytkownika'
      : 'Zarządzanie pieczątkami',
  );

  const loadStamps = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const user = await spd.users.getById(userId);
      setTitleText(
        isEditingOtherUser
          ? `Zarządzanie pieczątkami użytkownika ${user?.name}`
          : 'Zarządzanie pieczątkami',
      );
      const userStamps = await spd.stamps.getStampsByUser(userId);

      const bigStampData = userStamps.find(s => s.type === StampType.Big);
      const smallStampData = userStamps.find(s => s.type === StampType.Small);
      const doubleStampData = userStamps.find(s => s.type === StampType.Double);
      const signatureStampData = userStamps.find(
        s => s.type === StampType.Signature,
      );

      setBigStamp(getImageUrl(bigStampData) || '');
      setSmallStamp(getImageUrl(smallStampData) || '');
      setDoubleStamp(getImageUrl(doubleStampData) || '');
      setSignatureStamp(getImageUrl(signatureStampData) || '');
    } catch (err) {
      setError('Błąd podczas ładowania pieczątek: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [spd.stamps, userId, isEditingOtherUser, spd.users]);

  useEffect(() => {
    void loadStamps();
  }, [loadStamps]);

  const handleStampUpload = async () => {
    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const uploadData: UploadStampsDto = {};

      if (bigStamp && bigStamp.includes('base64,')) {
        uploadData.big = bigStamp.split('base64,')[1];
        if (calculateBase64Size(uploadData.big) > MAX_STAMP_SIZE_BYTES) {
          setError(
            `Rozmiar pliku "Pieczątka duża" jest za duży. Maksymalny rozmiar to ${MAX_KB_SIZE}kb.`,
          );
          setUploading(false);
          return;
        }
      }
      if (smallStamp && smallStamp.includes('base64,')) {
        uploadData.small = smallStamp.split('base64,')[1];
        if (calculateBase64Size(uploadData.small) > MAX_STAMP_SIZE_BYTES) {
          setError(
            `Rozmiar pliku "Pieczątka mała" jest za duży. Maksymalny rozmiar to ${MAX_KB_SIZE}kb.`,
          );
          setUploading(false);
          return;
        }
      }
      if (doubleStamp && doubleStamp.includes('base64,')) {
        uploadData._double = doubleStamp.split('base64,')[1];
        if (calculateBase64Size(uploadData._double) > MAX_STAMP_SIZE_BYTES) {
          setError(
            `Rozmiar pliku "Pieczątka podwójna" jest za duży. Maksymalny rozmiar to ${MAX_KB_SIZE}kb.`,
          );
          setUploading(false);
          return;
        }
      }
      if (signatureStamp && signatureStamp.includes('base64,')) {
        uploadData.signature = signatureStamp.split('base64,')[1];
        if (calculateBase64Size(uploadData.signature) > MAX_STAMP_SIZE_BYTES) {
          setError(
            `Rozmiar pliku "Podpis" jest za duży. Maksymalny rozmiar to ${MAX_KB_SIZE}kb.`,
          );
          setUploading(false);
          return;
        }
      }

      if (Object.keys(uploadData).length === 0) {
        setError('Nie wybrano żadnych pieczątek do przesłania');
        return;
      }

      await spd.stamps.uploadStamps(uploadData, userId);
      setSuccess('Pieczątki zostały zaktualizowane');
      await loadStamps();
    } catch (err) {
      setError('Błąd podczas przesyłania pieczątek: ' + (err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const getImageUrl = (stamp: GetStampDto | undefined): string => {
    if (!stamp || !stamp.image) {
      return '';
    }
    if (stamp.image?.startsWith('data:')) {
      return stamp.image;
    }
    const result = `data:image/png;base64,${stamp.image}`;
    return result;
  };

  const calculateBase64Size = (base64String: string): number => {
    return ((base64String.length || 0) * 3) / 4;
  };

  return (
    <Container>
      <Row className="justify-content-center">
        {loading && <Waiting />}
        <Col md={10}>
          <Card>
            <Card.Body>
              <h3 className="fw-normal mb-4">
                <Icon.PersonBadge className="me-2" />
                {titleText}
              </h3>

              <StampAlerts error={error} success={success} />

              <StampDisplay
                bigStamp={bigStamp}
                setBigStamp={setBigStamp}
                smallStamp={smallStamp}
                setSmallStamp={setSmallStamp}
                doubleStamp={doubleStamp}
                setDoubleStamp={setDoubleStamp}
                signatureStamp={signatureStamp}
                setSignatureStamp={setSignatureStamp}
              />

              <StampActions
                uploading={uploading}
                onUpload={handleStampUpload}
                onRefresh={() => void loadStamps()}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};
